import { Typography } from '../primitives/index.js'
import { logger } from '@tunasong/models'
import type { Persisted, Entity } from '@tunasong/schemas'
import { useCallback, useEffect, useRef } from 'react'
import type { ChangeEvent, CSSProperties, FC, HTMLAttributes } from 'react'
import { useDropzone } from 'react-dropzone'
import type { Accept, FileWithPath } from 'react-dropzone'
import { isMobileDevice } from '../browser/index.js'
import { useStableRef } from '../hooks/stable-ref.js'
import { useUploadFiles } from '../upload/upload-files.hook.js'
import { LinearProgress } from '../primitives/index.js'
import moduleStyles from './dropzone.module.css'

export interface DropzoneProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
  parentId: string | null
  accept: Accept
  tags?: string[]
  contentHint?: string
  showZone?: boolean
  showClickZone?: boolean
  isPrivate?: boolean
  uploadOnClick?: boolean
  /**
   * Keep the children rendered while files are dragged over the zone, and show the drag as an outline
   * instead of a padded box. For a zone that wraps a whole page, which must not blank or reflow the
   * moment a file crosses it. @default false
   */
  keepChildrenOnDrag?: boolean
  maxFiles?: number
  onUploading?(): void
  onUploaded?(uploaded: Persisted<Entity>): void
  /** An upload failed or was refused */
  onUploadError?(error: Error): void
}

export const DropZone: FC<DropzoneProps> = props => {
  const {
    className,
    parentId,
    accept,
    children,
    showZone = false,
    uploadOnClick = false,
    isPrivate = false,
    contentHint = 'file',
    maxFiles = 50,
    title,
    showClickZone,
    keepChildrenOnDrag = false,
    onUploaded,
    onUploading,
    onUploadError,
    tags,
    ...restProps
  } = props

  const { uploadFiles, uploading, handleChange } = useUploadFiles({ isPrivate, onUploaded, tags })
  const onUploadingRef = useStableRef(onUploading)
  useEffect(() => {
    if (uploading && onUploadingRef.current) {
      onUploadingRef.current()
    }
  }, [onUploadingRef, uploading])
  const handleError = (err: unknown) => {
    logger.error('Failed to upload files', err)
    onUploadError?.(err instanceof Error ? err : new Error('Failed to upload files', { cause: err }))
  }
  const onDrop = (acceptedFiles: FileWithPath[]) => {
    uploadFiles({ files: acceptedFiles, parentId }).catch(handleError)
  }
  const handleInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
    handleChange(parentId)(ev).catch(handleError)
  }
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    maxFiles,
    accept,
    noClick: true,
  })
  const uploadRef = useRef<HTMLInputElement | null>(null)
  const handleClick = useCallback(() => {
    if (showZone || uploadOnClick) {
      uploadRef.current?.click()
    }
  }, [showZone, uploadOnClick])
  const { ...rootProps } = getRootProps()
  const acceptStr =
    Object.keys(accept).join(',') +
    Object.values(accept)
      .flatMap(v => v)
      .join(',')
  const dropZoneText = title ?? (isMobileDevice() ? 'Tap to upload' : 'Drop files or click to upload')
  const isActive = isDragActive

  const baseStyle: CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 100,
    outline: 'none',
    marginBottom: 16,
    ...(showZone && {
      borderColor: 'var(--tuna-palette-divider)',
      borderWidth: 2,
      borderStyle: 'dashed',
    }),
    ...(isActive &&
      keepChildrenOnDrag && {
        outline: '2px dashed var(--tuna-palette-primary-main)',
        outlineOffset: 'var(--tuna-spacing-0_5)',
        ...(isDragAccept && { outlineColor: 'var(--tuna-palette-success-main)' }),
      }),
    ...(isActive &&
      !keepChildrenOnDrag && {
        marginTop: 8,
        justifyContent: 'center',
        padding: 16,
        borderColor: 'var(--tuna-palette-primary-main)',
        ...(isDragReject && { borderColor: 'var(--tuna-palette-divider)' }),
        ...(isDragAccept && {
          borderColor: 'var(--tuna-palette-success-main)',
          color: 'var(--tuna-palette-success-main)',
        }),
      }),
  }

  return (
    <>
      <div
        {...rootProps}
        className={className}
        style={baseStyle}
        tabIndex={-1}
        data-drag-active={isActive ? 'true' : undefined}
        title={uploadOnClick ? dropZoneText : undefined}
        onClick={uploadOnClick && !showClickZone ? handleClick : undefined}
        {...restProps}
      >
        <input
          {...getInputProps()}
          ref={uploadRef}
          disabled={uploading}
          type="file"
          multiple
          accept={acceptStr}
          hidden
          onChange={handleInputChange}
        />
        <Typography variant="caption" className={moduleStyles.captionText}>
          {isDragAccept && `All file(s) accepted. Drop to add ${contentHint}`}
          {isDragReject && 'Some files may be rejected'}
        </Typography>
        {keepChildrenOnDrag || !isDragActive ? <div>{children}</div> : null}
        {uploadOnClick && showClickZone && !isDragActive ? (
          <div onClick={handleClick} className={moduleStyles.clickZone}>
            <Typography variant="caption">{dropZoneText}</Typography>
          </div>
        ) : null}
      </div>
      {uploading ? <LinearProgress /> : null}
    </>
  )
}

export default DropZone
